import { FilterOption, FilterType } from 'interfaces/components/filter';
import { DisabledFilters } from 'interfaces/store/disabledFilters';
import {
  softwareSectorFilterOptions,
  growthMotionFilterOptions,
  primaryCustomerFilterOptions
} from 'utils/constants';
import { ChartFilters, ChartSwitches } from 'interfaces/store/chartFilters';
import { chartsToHideAvgDealSizeSwitch } from 'components/SectionCharts/ChartContainer';

// do not show ARR group data unless filtered results exceed this value
// const INSUFFICIENT_RESULTS_THRESHOLD_8 = 8;
export const NSIZE_8 = 8;
export const NSIZE_10 = 10;

export type CompanyMetrics = {
  currentARR: number | null;
  yoyARR: number | null;
  fcfMargin: number | null;
  avgDealSize: number | null;
  totalHeadcount: number | null;
  companyWebsite: string;
  companyColor: string;
  grossMargin: number | null;
  netDollarRetention: number | null;
  netMagicNumber: number | null;
  burnMultiple: number | null;
  quickRatio: number | null;
  rD: number | null;
  gA: number | null;
  sM: number | null;
  rDHead: number | null;
  gAHead: number | null;
  sMHead: number | null;
  otherHead: number | null;
  rDHeadPercent: number | null;
  gAHeadPercent: number | null;
  sMHeadPercent: number | null;
  otherHeadPercent: number | null;
  grossDollarRetention: number | null;
  newLogoVelocity: number | null;
  cacPaybackPeriod: number | null;
  opexPerFTE: number | null;
  newLogoArrOfGrossNew: number | null;
  expansionArrOfGrossNew: number | null;
  smPercent: number | null;
  gaPercent: number | null;
  rdPercent: number | null;
  revenue: number | null;
  ruleOf40: number | null;
  arrPerFTE: number | null;
};

export interface ChartDataMap {
  ARR_EOP: number;
  ARR_PERCTOTAL_CHURN_DOWNSELL: number | null;
  ARR_PERCTOTAL_CHURN_LOGO: number | null;
  ARR_PER_FTE: number;
  ARR_RANGE: string;
  ARR_YYGROWTH_EOP: number;
  BURN_MULTIPLE: number;
  EMPLOYEES_PERCTOTAL_GA: number | null;
  EMPLOYEES_PERCTOTAL_RD: number | null;
  EMPLOYEES_PERCTOTAL_SM: number | null;
  FCF: number;
  GEOGRAPHY: String;
  GROWTH_MOTION: string;
  INDUSTRY: String;
  MAGICNUMBER_NET: number;
  MARGIN_FCF: number;
  MARGIN_GROSSPROFIT: number;
  MARGIN_OPEX: number;
  MARGIN_OPEX_GA: number;
  MARGIN_OPEX_RD: number;
  MARGIN_OPEX_SM: number;
  PERIODEND: string;
  PRIMARY_CUSTOMER_TARGET: string;
  QUICK_RATIO_ARR: number;
  RETENTION_NET_ARR_ANNUALIZED: number;
  RULE_OF_40: number;
  SALES_MOTION: string;
  SECTOR: string;
  LAST_4_YEAR_FILTER: boolean;
  ARR_PERCTOTAL_GROSSNEW_EXPANSION: number;
  ARR_PERCTOTAL_GROSSNEW_NEWLOGO: number;
  NEW_LOGO_VELOCITY: number;
  RETENTION_GROSS_ARR_ANNUALIZED: number;
  CAC_PAYBACK: number;
  OPEX_PER_FTE: number;
  AVG_CONTRACT_VALUE: number;
  AVG_DEAL_SIZE: string;
}

export const ChartsARRRangeLabels = [
  '<$25M',
  '$25-$50M',
  '$50-$100M',
  '$100-$200M',
  '>$200M'
];

export const ChartsAvgDealSizeLabels = [
  '<$10K',
  '$10-50K',
  '$50-150K',
  '$150K+'
];

export enum sortDir {
  ASC = 'asc',
  DESC = 'desc'
}

// generate the array for each graph
export const mapCalculatedData = (
  xAxisSwitch: boolean,
  map: Map<string, number[]>,
  nSize: number,
  dir?: sortDir
) => {
  const quartileArray: (number | null)[] = [];
  const medianArray: (number | null)[] = [];
  const averageArray: (number | null)[] = [];
  const labels = xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels;

  if (dir === undefined) dir = sortDir.ASC;

  labels.forEach(label => {
    const chartArray = map.get(label);
    // if (chartArray && chartArray?.length > INSUFFICIENT_RESULTS_THRESHOLD) {
    if (chartArray && chartArray?.length > nSize) {
      const quartile = calculateQuartile(chartArray || [], dir);
      const median = calculateMedian(chartArray || []);
      const average = calculateAverage(chartArray || []);
      quartileArray.push(quartile !== undefined ? quartile : null);
      medianArray.push(median !== undefined ? median : null);
      averageArray.push(average !== undefined ? average : null);
    } else {
      quartileArray.push(null);
      medianArray.push(null);
      averageArray.push(null);
    }
  });

  return {
    quartileArray,
    medianArray,
    averageArray
  };
};

export const applyFiltersToChartData = (
  chartData: ChartDataMap[],
  chartFilters: ChartFilters,
  chartSwitches: ChartSwitches
): ChartDataMap[] => {
  return chartData.filter(data => {
    const matchesGrowthMotion =
      chartFilters.growthMotion.length > 0
        ? chartFilters.growthMotion.some(option =>
            option.value.includes(data.GROWTH_MOTION)
          )
        : true;

    const matchesIndustry =
      chartFilters.softwareSector.length > 0
        ? chartFilters.softwareSector.some(option =>
            option.value.includes(data.SECTOR)
          )
        : true;

    const matchesCustomerTarget =
      chartFilters.primaryCustomer.length > 0
        ? chartFilters.primaryCustomer.some(option =>
            option.value.includes(data.PRIMARY_CUSTOMER_TARGET)
          )
        : true;

    const matchesTimeRange =
      chartSwitches.timeDurationSwitch || data.LAST_4_YEAR_FILTER;

    return (
      matchesGrowthMotion &&
      matchesIndustry &&
      matchesCustomerTarget &&
      matchesTimeRange
    );
  });
};

//checks which label the value goes to
export const getARRIndex = (currentARR: number): number => {
  if (currentARR < 25000000) return 0;
  if (currentARR >= 25000000 && currentARR < 50000000) return 1;
  if (currentARR >= 50000000 && currentARR < 100000000) return 2;
  if (currentARR >= 100000000 && currentARR < 200000000) return 3;
  return 4;
};

export const getAvgDealSizeIndex = (avgDealSize: number): number => {
  if (avgDealSize < 10000) return 0;
  if (avgDealSize >= 10000 && avgDealSize < 50000) return 1;
  if (avgDealSize >= 50000 && avgDealSize < 150000) return 2;
  return 3;
};

// defaults to "inclusive" mode
// optional `exclusive` boolean param to use "exclusive" mode
const calculateQuartile = (
  data: number[],
  dir: sortDir = sortDir.ASC,
  exclusive?: boolean
): number | undefined => {
  return exclusive
    ? calculateQuartileExlusive(data, dir)
    : calculateQuartileInclusive(data, dir);
};

// this is an "inclusive" version of caculating Quartile that includes the median value
const calculateQuartileInclusive = (
  data: number[],
  dir: sortDir = sortDir.ASC
): number | undefined => {
  const filteredData = data.filter(
    value => value !== undefined && !isNaN(value) && value !== null
  );

  if (filteredData.length === 0) return undefined;

  // Sort the data array
  // const sortedData = filteredData.slice().sort((a, b) => a - b);

  const sortDirection =
    dir === sortDir.ASC
      ? (a: number, b: number) => a - b
      : (a: number, b: number) => b - a;
  const sortedData = filteredData.slice().sort(sortDirection);

  const n = sortedData.length;
  const q3Index = 0.75 * (n - 1);

  // Calculate the third quartile (75th percentile)
  if (q3Index % 1 === 0) {
    // Check if q3Index is an integer
    return sortedData[q3Index];
  } else {
    // Interpolate between the two nearest points
    const lower = Math.floor(q3Index);
    const upper = Math.ceil(q3Index);
    return (
      sortedData[lower] +
      (sortedData[upper] - sortedData[lower]) * (q3Index - lower)
    );
  }
};

// this is an "exclsive" version of caculating Quartile that excludes the median value
const calculateQuartileExlusive = (
  data: number[],
  dir: sortDir = sortDir.ASC
): number | undefined => {
  const filteredData = data.filter(
    value => value !== undefined && !isNaN(value) && value !== null
  );

  if (filteredData.length === 0) return undefined;

  // const sortedArray = filteredData.sort((a, b) => a - b);
  const sortDirection =
    dir === sortDir.ASC
      ? (a: number, b: number) => a - b
      : (a: number, b: number) => b - a;
  const sortedArray = filteredData.slice().sort(sortDirection);

  const quartileIndex = Math.ceil((3 * sortedArray.length) / 4) - 1;

  return sortedArray[quartileIndex];
};

const calculateMedian = (arr: number[]): number | undefined => {
  const filteredArray = arr.filter(
    value => value !== undefined && !isNaN(value) && value !== null
  );

  if (filteredArray.length === 0) return undefined;

  const sortedArray = filteredArray.sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedArray.length / 2);

  if (sortedArray.length % 2 === 0) {
    return (sortedArray[middleIndex - 1] + sortedArray[middleIndex]) / 2;
  } else {
    return sortedArray[middleIndex];
  }
};

const calculateAverage = (data: number[]): number | undefined => {
  const filteredData = data.filter(
    value => value !== undefined && !isNaN(value) && value !== null
  );

  if (filteredData.length === 0) return undefined;

  const sum = filteredData.reduce((acc, value) => acc + value, 0);

  const average = sum / filteredData.length;

  return average;
};

const memoizedFilterOptions = {
  softwareSector: softwareSectorFilterOptions,
  growthMotion: growthMotionFilterOptions,
  primaryCustomer: primaryCustomerFilterOptions
};

const chartTitleToKeyMap: Record<string, keyof ChartDataMap> = {
  'YoY ARR Growth': 'ARR_YYGROWTH_EOP',
  'Net Magic Number': 'MAGICNUMBER_NET',
  'Gross Margin': 'MARGIN_GROSSPROFIT',
  'Burn Multiple': 'BURN_MULTIPLE',
  'ARR per FTE': 'ARR_PER_FTE',
  'FCF Margin': 'MARGIN_FCF',
  'Quick Ratio': 'QUICK_RATIO_ARR',
  'Gross Dollar Retention': 'RETENTION_GROSS_ARR_ANNUALIZED',
  'Net Dollar Retention': 'RETENTION_NET_ARR_ANNUALIZED',
  'OpEx as a % of Revenue': 'MARGIN_OPEX',
  'Headcount Distribution': 'EMPLOYEES_PERCTOTAL_RD',
  'Gross New ARR Composition': 'ARR_PERCTOTAL_GROSSNEW_NEWLOGO',
  'New Logo Velocity': 'NEW_LOGO_VELOCITY',
  'CAC Payback': 'CAC_PAYBACK',
  'OpEx per FTE': 'OPEX_PER_FTE',
  'Rule of 40': 'RULE_OF_40'
};

const processFilterOptions = (
  chartData: ChartDataMap[],
  options: FilterOption[],
  filterFn: (item: ChartDataMap, option: FilterOption) => boolean
): Map<string, ChartDataMap[]> => {
  const filterMap = new Map<string, ChartDataMap[]>();
  options.forEach(option => {
    const filteredData = chartData.filter(item => filterFn(item, option));
    if (filteredData.length) {
      filterMap.set(option.value, filteredData);
    }
  });
  return filterMap;
};

const getFilteredDataMap = (chartData: ChartDataMap[]) => {
  const filteredDataMap = new Map<string, Map<string, ChartDataMap[]>>();

  filteredDataMap.set(
    'software_sector',
    processFilterOptions(
      chartData,
      memoizedFilterOptions.softwareSector,
      (item, option) => item.SECTOR === option.value
    )
  );

  filteredDataMap.set(
    'growth_motion',
    processFilterOptions(
      chartData,
      memoizedFilterOptions.growthMotion,
      (item, option) => item.GROWTH_MOTION === option.value
    )
  );

  filteredDataMap.set(
    'primary_customer',
    processFilterOptions(
      chartData,
      memoizedFilterOptions.primaryCustomer,
      (item, option) => item.PRIMARY_CUSTOMER_TARGET === option.value
    )
  );

  return filteredDataMap;
};

const checkFilterOption = (
  xAxisSwitch: boolean,
  filterOptions: FilterOption[],
  filterKey: FilterType,
  filteredDataMap: Map<string, Map<string, ChartDataMap[]>>,
  dataKey: keyof ChartDataMap
): boolean => {
  if (!filterOptions.length) return false;

  const labels = xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels;

  return filterOptions.some(option => {
    const filteredData = filteredDataMap.get(filterKey)?.get(option.value);
    if (!filteredData) return true;

    const rangeCounts = new Map<string, number>();
    filteredData.forEach(item => {
      const range = xAxisSwitch ? item.ARR_RANGE : item.AVG_DEAL_SIZE;
      const value = item[dataKey];

      if (value !== null && typeof value === 'number') {
        rangeCounts.set(range, (rangeCounts.get(range) || 0) + 1);
      }
    });
    if (rangeCounts.size !== labels.length) return false;

    const isQuickRatioOrNetMagic =
      dataKey === 'QUICK_RATIO_ARR' || dataKey === 'MAGICNUMBER_NET';
    const threshold = isQuickRatioOrNetMagic ? NSIZE_10 : NSIZE_8;
    return Array.from(rangeCounts.values()).some(count => count <= threshold);
  });
};

const getDisabledFiltersForChart = (
  xAxisSwitch: boolean,
  chartTitle: string,
  filteredDataMap: Map<string, Map<string, ChartDataMap[]>>
): DisabledFilters => {
  const dataKey = chartTitleToKeyMap[chartTitle];
  if (!dataKey) {
    console.warn(`Unknown chart title: ${chartTitle}`);
    return {
      softwareSector: true,
      growthMotion: true,
      primaryCustomer: true
    };
  }

  return {
    softwareSector: checkFilterOption(
      xAxisSwitch,
      memoizedFilterOptions.softwareSector,
      'software_sector',
      filteredDataMap,
      dataKey
    ),
    growthMotion: checkFilterOption(
      xAxisSwitch,
      memoizedFilterOptions.growthMotion,
      'growth_motion',
      filteredDataMap,
      dataKey
    ),
    primaryCustomer: checkFilterOption(
      xAxisSwitch,
      memoizedFilterOptions.primaryCustomer,
      'primary_customer',
      filteredDataMap,
      dataKey
    )
  };
};

export const generateDisabledFiltersMap = (
  xAxisSwitch: boolean,
  unfilteredData: ChartDataMap[]
) => {
  const filteredDataMap = getFilteredDataMap(unfilteredData);

  const map = Object.keys(chartTitleToKeyMap).reduce<
    Record<string, DisabledFilters>
  >((acc, title) => {
    const isXAxisSwitchDisabled = chartsToHideAvgDealSizeSwitch.includes(title);

    acc[title] = getDisabledFiltersForChart(
      isXAxisSwitchDisabled ? true : xAxisSwitch,
      title,
      filteredDataMap
    );
    return acc;
  }, {});

  return map;
};

export const getAvgDealSizeLabel = (avgContractValue: number) => {
  if (!avgContractValue) return 'N/A';
  if (avgContractValue < 10000) return '<$10K';
  if (avgContractValue < 50000) return '$10-50K';
  if (avgContractValue < 150000) return '$50-150K';
  return '$150K+';
};

export const allValuesAreNull = (arr: any[]): boolean => {
  return arr.every(value => value === null);
};

export const generateBubbleData = (
  arrYygrowthEOP: (number | null)[],
  marginFCF: (number | null)[],
  ruleOf40Array: (number | null)[]
) => {
  const dataSet = [];

  for (
    let i = 0;
    i <
    Math.min(arrYygrowthEOP.length, marginFCF.length, ruleOf40Array.length, 5);
    i++
  ) {
    const x = arrYygrowthEOP[i];
    const y = marginFCF[i];
    const ro40 = ruleOf40Array[i];

    dataSet.push({
      x: x != null ? x * 100.0 : x,
      y: y != null ? y * 100.0 : y,
      r:
        i === 0
          ? 10
          : i === 1
          ? 20
          : i === 2
          ? 30
          : i === 3
          ? 40
          : i === 4
          ? 50
          : null,
      ruleOf40: ro40 != null ? ro40 * 100.0 : ro40
    });
  }

  // return dataSet; // temp until we refactor
  return dataSet.filter(object => object.r !== 10 && object.ruleOf40 !== null);
};

export const generateBubbleDataCompany = (
  arrIndex: number,
  companyMetrics: CompanyMetrics
) => {
  let radio = null;

  radio =
    arrIndex === 0
      ? 10
      : arrIndex === 1
      ? 20
      : arrIndex === 2
      ? 30
      : arrIndex === 3
      ? 40
      : arrIndex === 4
      ? 50
      : null;

  const x = companyMetrics.yoyARR === 0 ? null : companyMetrics.yoyARR;
  const y = companyMetrics.fcfMargin === 0 ? null : companyMetrics.fcfMargin;
  const r = companyMetrics.yoyARR === 0 ? null : radio;
  const ruleOf40 =
    companyMetrics.ruleOf40 === 0 ? null : companyMetrics.ruleOf40;

  const dataSet = [{ x, y, r, ruleOf40 }];

  return dataSet;
};

export const yourCompanyFormatted = (
  arrIndex: number,
  xAxisSwitch: boolean,
  value: number,
  isDollar?: boolean
) => {
  let arr = [];

  const labels = xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels;

  if (value && !isDollar) {
    arr = Array(labels.length)
      .fill(null)
      .map((_, i) => (i === arrIndex ? value : null));
  } else if (value && isDollar === true) {
    arr = Array(labels.length)
      .fill(null)
      .map((_, i) => (i === arrIndex ? Math.round(value / 1000) : null));
  } else {
    arr = Array(labels.length).fill(null);
  }

  return arr;
};
